import {
  useState,
  type ReactNode,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type Modifier,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  findNearestFreePosition as unifiedFindNearestFreePosition,
  EDGE_INSET_RATIO,
  EDGE_INSET_MAX,
} from "@/utils/desktop-layout-utils";
import { renderWidget } from "@/components/widgets/WidgetRenderer";
import { WidgetPreviewProvider } from "@/components/widgets/WidgetWrapper";
import { useWidgetSettings } from "@/contexts/WidgetSettingsContext";
import { useWidgetArea } from "@/contexts/WidgetAreaContext";
import { useWindows } from "@/hooks/useWindows";
import type {
  WidgetConfig,
  WidgetId,
  WidgetSize,
  WidgetDesktopPosition,
} from "@/types/widget";
import { WIDGET_GRID_SIZE } from "@/types/widget";
import {
  determineSidebarDropIndex,
  getSidebarPanelElement,
} from "@/components/widgets/sidebarDropUtils";
import {
  useDesktopGrid,
  type DesktopGridState,
} from "@/contexts/DesktopGridContext";
import {
  useWidgetDragMetadata,
  useWidgetDragMovement,
  type WidgetDragSource,
} from "@/contexts/WidgetDragContext";
import { useGridDrag } from "@/contexts/GridDragContext";

export const SIDEBAR_ZONE_ID = "sidebar-zone";
export const DESKTOP_ZONE_ID = "desktop-zone";
export const STORE_ZONE_ID = "store-zone";

export type DesktopDragType = "widget" | "icon";

export type WidgetDragData = {
  type: DesktopDragType;
  id: string; // widgetId for widgets, appId for icons
  source: WidgetDragSource;
  size: WidgetSize | "icon";
  widgetId?: WidgetId; // Keep for backward compatibility
};

const SIDEBAR_WIDGET_PREVIEW_SIZE: Record<
  WidgetSize | "icon",
  { width: number; height: number }
> = {
  small: { width: 168, height: 140 },
  medium: { width: 352, height: 140 },
  tall: { width: 168, height: 296 },
  large: { width: 352, height: 296 },
  icon: { width: 80, height: 100 },
};

interface WidgetDndProviderProps {
  children: ReactNode;
}

function getWidgetConfig(
  widgetId: WidgetId,
  configs: WidgetConfig[]
): WidgetConfig | undefined {
  return configs.find((config) => config.id === widgetId);
}

// Helper to extract widget ID from draggable IDs
const extractWidgetId = (id: string): WidgetId | null => {
  if (id.startsWith("sidebar-widget-")) {
    return id.replace("sidebar-widget-", "") as WidgetId;
  }
  if (id.startsWith("desktop-widget-")) {
    return id.replace("desktop-widget-", "") as WidgetId;
  }
  if (id.startsWith("widget-store-card-")) {
    return id.replace("widget-store-card-", "") as WidgetId;
  }
  return null;
};

// Helper to determine drop zone from over ID
const getDropZone = (overId: string | number | null): string | null => {
  if (!overId || typeof overId !== "string") return null;
  if (overId === SIDEBAR_ZONE_ID) return "sidebar";
  if (overId === DESKTOP_ZONE_ID) return "desktop";
  if (overId === STORE_ZONE_ID) return "store";
  if (overId.startsWith("sidebar-widget-")) return "sidebar";
  if (overId.startsWith("desktop-widget-")) return "desktop";
  if (overId.startsWith("widget-store-card-")) return "store";
  return null;
};

type ClientPoint = { x: number; y: number };

const SIDEBAR_EDGE_TRIGGER_PX = 50;
const SIDEBAR_FALLBACK_WIDTH = 400;
const SIDEBAR_TASKBAR_HEIGHT = 64;
const SIDEBAR_DETECTION_PADDING = 20;
const SIDEBAR_POINT_SAFE_OFFSET = 16;

type SidebarBounds = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

function extractClientPoint(
  event: Event | null | undefined
): ClientPoint | null {
  if (!event) return null;
  if ("clientX" in event && "clientY" in event) {
    const pointEvent = event as MouseEvent | PointerEvent;
    return {
      x: pointEvent.clientX,
      y: pointEvent.clientY,
    };
  }
  return null;
}

function getSidebarBounds(isSidebarOpen: boolean): SidebarBounds | null {
  if (typeof window === "undefined") return null;
  const sidebarPanel = getSidebarPanelElement();
  if (sidebarPanel) {
    const rect = sidebarPanel.getBoundingClientRect();
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
    };
  }

  if (isSidebarOpen) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    return {
      left: screenWidth - SIDEBAR_FALLBACK_WIDTH,
      right: screenWidth,
      top: 0,
      bottom: screenHeight - SIDEBAR_TASKBAR_HEIGHT,
    };
  }

  return null;
}

function isPointInsideSidebarArea(
  point: ClientPoint | null,
  isSidebarOpen: boolean
): boolean {
  if (!point) return false;
  const bounds = getSidebarBounds(isSidebarOpen);
  if (!bounds) return false;

  return (
    point.x >= bounds.left - SIDEBAR_DETECTION_PADDING &&
    point.x <= bounds.right + SIDEBAR_DETECTION_PADDING &&
    point.y >= bounds.top - SIDEBAR_DETECTION_PADDING &&
    point.y <= bounds.bottom + SIDEBAR_DETECTION_PADDING
  );
}

function isPointNearSidebarEdge(point: ClientPoint | null): boolean {
  if (!point || typeof window === "undefined") return false;
  return window.innerWidth - point.x <= SIDEBAR_EDGE_TRIGGER_PX;
}

function clampPointToSidebar(
  point: ClientPoint | null,
  isSidebarOpen: boolean
): ClientPoint | null {
  const bounds = getSidebarBounds(isSidebarOpen);
  if (!bounds) return point;

  const candidate = point ?? {
    x: (bounds.left + bounds.right) / 2,
    y: (bounds.top + bounds.bottom) / 2,
  };

  return {
    x: Math.min(
      bounds.right - SIDEBAR_POINT_SAFE_OFFSET,
      Math.max(bounds.left + SIDEBAR_POINT_SAFE_OFFSET, candidate.x)
    ),
    y: Math.min(
      bounds.bottom - SIDEBAR_POINT_SAFE_OFFSET,
      Math.max(bounds.top + SIDEBAR_POINT_SAFE_OFFSET, candidate.y)
    ),
  };
}

export function WidgetDndProvider({ children }: WidgetDndProviderProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Small movement to activate drag (prevents accidental drags on click)
      },
      bypassActivationConstraint: ({ event }) => {
        const target = event.target as HTMLElement | null;
        return !!target?.closest("[data-dnd-fast]");
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: {
        start: ["Enter"],
        cancel: ["Escape"],
        end: ["Enter"],
      },
    })
  );

  const {
    widgetConfigs,
    moveWidgetToSidebar,
    moveWidgetWithinSidebar,
    moveWidgetToDesktop,
    disableWidget,
    updateWidgetDesktopPosition,
  } = useWidgetSettings();
  const { open: openWidgetArea, isOpen: isWidgetAreaOpen } = useWidgetArea();
  const { windows } = useWindows();

  const { activeWidget, setActiveWidget, activeItem, setActiveItem } =
    useWidgetDragMetadata();
  const {
    setCurrentDesktopDropPosition,
    currentSidebarDropIndex,
    setCurrentSidebarDropIndex,
  } = useWidgetDragMovement();
  const { setGridDragActive, setHoveredGridPosition, setDraggedItemSize } =
    useGridDrag();
  const [isDraggingDesktopWidget, setIsDraggingDesktopWidget] = useState(false);
  const [dragOverlayRect, setDragOverlayRect] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const shouldHideDragOverlay =
    activeItem?.type === "widget" &&
    activeItem.source === "store" &&
    currentSidebarDropIndex !== null;
  const cursorPositionRef = useRef({ x: 0, y: 0 });
  const lastPointerEventTsRef = useRef(0);
  const dragStartPointerRef = useRef<ClientPoint | null>(null);
  const grabOffsetRef = useRef<ClientPoint | null>(null);
  const edgeHotzoneTimeoutRef = useRef<number | null>(null);
  const sidebarDropIndexRef = useRef<number | null>(null);
  const sidebarDropRafRef = useRef<number | null>(null);
  // Track cursor position globally
  const handlePointerMove = useCallback((event: PointerEvent) => {
    cursorPositionRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    lastPointerEventTsRef.current = Date.now();
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    cursorPositionRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    lastPointerEventTsRef.current = Date.now();
  }, []);

  const handleTouchMove = useCallback((event: TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    cursorPositionRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    lastPointerEventTsRef.current = Date.now();
  }, []);

  // Set up global pointer tracking
  useEffect(() => {
    if (typeof document === "undefined") return;
    const target = document;
    target.addEventListener("pointermove", handlePointerMove);
    target.addEventListener("mousemove", handleMouseMove);
    target.addEventListener("touchmove", handleTouchMove, { passive: true });
    return () => {
      target.removeEventListener("pointermove", handlePointerMove);
      target.removeEventListener("mousemove", handleMouseMove);
      target.removeEventListener("touchmove", handleTouchMove);
    };
  }, [handlePointerMove, handleMouseMove, handleTouchMove]);

  const { gridState } = useDesktopGrid();

  const scheduleSidebarDropIndex = useCallback(
    (index: number | null) => {
      sidebarDropIndexRef.current = index;
      if (typeof window === "undefined") {
        setCurrentSidebarDropIndex(index);
        return;
      }
      if (sidebarDropRafRef.current) {
        return;
      }
      sidebarDropRafRef.current = window.requestAnimationFrame(() => {
        sidebarDropRafRef.current = null;
        setCurrentSidebarDropIndex(sidebarDropIndexRef.current);
      });
    },
    [setCurrentSidebarDropIndex]
  );

  const flushSidebarDropIndex = useCallback(
    (index: number | null) => {
      sidebarDropIndexRef.current = index;
      if (sidebarDropRafRef.current && typeof window !== "undefined") {
        window.cancelAnimationFrame(sidebarDropRafRef.current);
        sidebarDropRafRef.current = null;
      }
      setCurrentSidebarDropIndex(index);
    },
    [setCurrentSidebarDropIndex]
  );

  // Create a global boundary modifier for desktop widgets
  const boundaryModifier: Modifier = useMemo(() => {
    return ({ transform, active }) => {
      if (!active || !gridState) {
        return transform;
      }

      const data = active.data.current as WidgetDragData | undefined;
      // Use the config already stored in the activeItem if possible
      const config = activeItem?.config;

      if (
        !data ||
        !gridState ||
        data.source !== "desktop" ||
        (data.type === "widget" && !config)
      )
        return transform;

      const itemSize =
        data.size === "icon"
          ? { rows: 1, cols: 1 }
          : WIDGET_GRID_SIZE[data.size as WidgetSize];
      const { cellWidth, cellHeight, cols, rows } = gridState;

      // For icons, we need to know their starting grid position
      const startPos =
        data.type === "widget"
          ? config?.desktopPosition
          : (active.data.current as any).startPos;

      if (!startPos) {
        return transform;
      }

      const isWidget = data.type === "widget";
      const iconOffsetX = (cellWidth - 80) / 2;
      const iconOffsetY = (cellHeight - 100) / 2;

      // Widget/Icon specific offsets matching gridToPixels
      const areaWidth = itemSize.cols * cellWidth;
      const areaHeight = itemSize.rows * cellHeight;

      let offsetX = 0;
      let offsetY = 0;

      if (isWidget) {
        const insetX = Math.min(cellWidth * EDGE_INSET_RATIO, EDGE_INSET_MAX);
        const insetY = Math.min(cellHeight * EDGE_INSET_RATIO, EDGE_INSET_MAX);
        const widgetWidth = Math.max(areaWidth - insetX * 2, areaWidth * 0.85);
        const widgetHeight = Math.max(
          areaHeight - insetY * 2,
          areaHeight * 0.85
        );
        offsetX = (areaWidth - widgetWidth) / 2;
        offsetY = (areaHeight - widgetHeight) / 2;
      } else {
        offsetX = iconOffsetX;
        offsetY = iconOffsetY;
      }

      // Convert grid position to pixel coordinates
      // NOTE: We do NOT add padding here because absolute children start inside the padding box
      const currentX = startPos.col * cellWidth + offsetX;
      const currentY = startPos.row * cellHeight + offsetY;

      // Calculate max allowed pixel position
      const maxX = Math.max(
        offsetX,
        (cols - itemSize.cols) * cellWidth + offsetX
      );
      const maxY = Math.max(
        offsetY,
        (rows - itemSize.rows) * cellHeight + offsetY
      );

      const newX = currentX + transform.x;
      const newY = currentY + transform.y;

      const boundedX = Math.max(offsetX, Math.min(newX, maxX));
      const boundedY = Math.max(offsetY, Math.min(newY, maxY));

      return {
        ...transform,
        x: boundedX - currentX,
        y: boundedY - currentY,
      };
    };
  }, [gridState, activeItem]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current as WidgetDragData | undefined;
      if (!data) return;

      const { type, id, source, size, widgetId } = data;

      // Set active item for unified grid logic
      setActiveItem({
        id,
        type,
        size,
        source,
        config: widgetId ? getWidgetConfig(widgetId, widgetConfigs) : undefined,
      });

      if (type === "widget") {
        const config = widgetId
          ? getWidgetConfig(widgetId, widgetConfigs)
          : null;
        setActiveWidget(config ?? null);
        if (config?.desktopPosition) {
          event.active.data.current = {
            ...data,
            startPos: config.desktopPosition,
          };
        }
      } else {
        setActiveWidget(null);
        // Dispatch custom event to get icon position if needed, or use the one from drag start
        // For now, let's assume we can find it in the DOM or from a data attribute if we're clever,
        // but better to just look it up.
        window.dispatchEvent(
          new CustomEvent("request-icon-position", {
            detail: {
              appId: id,
              callback: (pos: any) => {
                event.active.data.current = { ...data, startPos: pos };
              },
            },
          })
        );
      }

      const initialPointer = extractClientPoint(event.activatorEvent);
      const initialRect = event.active.rect.current.initial;

      if (source === "sidebar" && initialRect) {
        setDragOverlayRect({
          width: initialRect.width,
          height: initialRect.height,
        });
      } else {
        setDragOverlayRect(null);
      }

      if (initialPointer) {
        cursorPositionRef.current = initialPointer;
        dragStartPointerRef.current = initialPointer;

        if (initialRect && typeof initialRect.left === "number") {
          grabOffsetRef.current = {
            x: initialPointer.x - initialRect.left,
            y: initialPointer.y - initialRect.top,
          };
        }
      } else {
        if (initialRect && typeof initialRect.left === "number") {
          const fallbackPoint = {
            x: initialRect.left + initialRect.width / 2,
            y: initialRect.top + initialRect.height / 2,
          };
          cursorPositionRef.current = fallbackPoint;
          dragStartPointerRef.current = fallbackPoint;
          grabOffsetRef.current = {
            x: initialRect.width / 2,
            y: initialRect.height / 2,
          };
        } else {
          dragStartPointerRef.current = null;
          grabOffsetRef.current = null;
        }
      }

      // Track if dragging a desktop item
      setIsDraggingDesktopWidget(source === "desktop");

      // Activate grid overlay
      setGridDragActive(true);
      setDraggedItemSize(size);

      // Open widget area when dragging from store
      if (source === "store") {
        openWidgetArea();
      }
    },
    [
      openWidgetArea,
      setActiveWidget,
      setActiveItem,
      setDragOverlayRect,
      setGridDragActive,
      setDraggedItemSize,
      widgetConfigs,
    ]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const data = event.active.data.current as WidgetDragData | undefined;
      const sidebarDropIndex = sidebarDropIndexRef.current;

      setActiveWidget(null);
      setActiveItem(null);
      setCurrentDesktopDropPosition(null);
      setDragOverlayRect(null);
      flushSidebarDropIndex(null);
      setIsDraggingDesktopWidget(false);

      // Deactivate grid overlay
      setGridDragActive(false);
      setHoveredGridPosition(null);
      setDraggedItemSize(null);

      if (edgeHotzoneTimeoutRef.current) {
        clearTimeout(edgeHotzoneTimeoutRef.current);
        edgeHotzoneTimeoutRef.current = null;
      }

      if (!data) {
        return;
      }

      let overId = event.over?.id;
      const activeRect = event.active.rect.current;
      const resolvedRect = activeRect.translated ?? activeRect.initial;
      const pointerFromDelta = dragStartPointerRef.current
        ? {
            x: dragStartPointerRef.current.x + event.delta.x,
            y: dragStartPointerRef.current.y + event.delta.y,
          }
        : null;
      const initialRect = event.active.rect.current.initial;
      const rectWithDelta =
        initialRect && typeof initialRect.left === "number"
          ? {
              x: initialRect.left + initialRect.width / 2 + event.delta.x,
              y: initialRect.top + initialRect.height / 2 + event.delta.y,
            }
          : null;
      const rectFallback =
        resolvedRect && typeof resolvedRect.left === "number"
          ? {
              x: resolvedRect.left + resolvedRect.width / 2,
              y: resolvedRect.top + resolvedRect.height / 2,
            }
          : null;
      let dropPoint =
        cursorPositionRef.current ??
        pointerFromDelta ??
        rectWithDelta ??
        rectFallback ??
        extractClientPoint(event.activatorEvent);

      let dropZone = getDropZone(overId ?? null);

      // Widget Store is only a valid drop target if the window exists and is not minimized
      const widgetStoreWindow = windows.find(
        (w) => w.appId === "widget-settings"
      );
      const isWidgetStoreValidDropTarget =
        widgetStoreWindow && !widgetStoreWindow.minimized;
      if (dropZone === "store" && !isWidgetStoreValidDropTarget) {
        // Widget Store is minimized or closed - treat as desktop drop instead
        dropZone = "desktop";
        overId = undefined;
      }

      if (data.source === "store") {
        const cursor = dropPoint ?? cursorPositionRef.current ?? null;
        const cursorInSidebar = isPointInsideSidebarArea(
          cursor,
          isWidgetAreaOpen
        );
        const shouldForceSidebar =
          dropZone === "sidebar" ||
          cursorInSidebar ||
          sidebarDropIndex !== null;

        if (shouldForceSidebar) {
          dropZone = "sidebar";
          overId = SIDEBAR_ZONE_ID;
          const safePoint = clampPointToSidebar(cursor, isWidgetAreaOpen);
          if (safePoint) {
            dropPoint = safePoint;
          }
        }
      }

      // If dragging from desktop, force sidebar drop when cursor is within/near the sidebar region,
      // even if the draggable is clamped by the desktop boundary modifier.
      if (data.source === "desktop") {
        const cursor = dropPoint ?? cursorPositionRef.current ?? null;
        const cursorInSidebar = isPointInsideSidebarArea(
          cursor,
          isWidgetAreaOpen
        );
        const cursorNearSidebarEdge = isPointNearSidebarEdge(cursor);
        // Nur dann Sidebar erzwingen, wenn wir wirklich im/über dem Sidebar-Bereich sind
        // oder die Sidebar offen ist und die Erkennung greift. Nähe zum rechten Rand allein
        // soll kein Sidebar-Drop auslösen (sonst landen Widgets im letzten Grid fälschlich in der Sidebar).
        const shouldForceSidebar =
          dropZone === "sidebar" ||
          cursorInSidebar ||
          (cursorNearSidebarEdge && isWidgetAreaOpen);

        if (shouldForceSidebar) {
          dropZone = "sidebar";
          overId = SIDEBAR_ZONE_ID;
          const safePoint = clampPointToSidebar(cursor, isWidgetAreaOpen);
          if (safePoint) {
            dropPoint = safePoint;
          }
        } else {
          dropZone = "desktop";
        }
      }

      const { widgetId, source, size } = data;

      const resolveDesktopPosition = () =>
        calculateDesktopDropPosition(
          dropPoint,
          size,
          gridState,
          grabOffsetRef.current,
          data.id,
          data.type
        );

      if (source === "store" && dropZone === "sidebar") {
        const sidebarWidgets = widgetConfigs.filter(
          (w) => w.enabled && !w.pinnedToDesktop
        );
        const targetIndex =
          sidebarDropIndex ??
          getSidebarDropIndex(overId ?? null, sidebarWidgets, dropPoint ?? undefined);
        moveWidgetToSidebar(widgetId as WidgetId, targetIndex);
        return;
      }

      if (source === "store" && dropZone === "desktop") {
        const desktopPos = resolveDesktopPosition();
        if (desktopPos) {
          moveWidgetToDesktop(widgetId as WidgetId, desktopPos);
        }
        return;
      }

      if (source === "sidebar" && dropZone === "desktop") {
        const desktopPos = resolveDesktopPosition();
        if (desktopPos) {
          moveWidgetToDesktop(widgetId as WidgetId, desktopPos);
        }
        return;
      }

      if (source === "desktop" && dropZone === "sidebar") {
        const sidebarWidgets = widgetConfigs.filter(
          (w) => w.enabled && !w.pinnedToDesktop
        );
        const targetIndex =
          sidebarDropIndex ??
          getSidebarDropIndex(overId ?? null, sidebarWidgets, dropPoint ?? undefined);
        moveWidgetToSidebar(widgetId as WidgetId, targetIndex);
        return;
      }

      if (source === "desktop" && dropZone === "desktop") {
        if (data.type === "widget") {
          const desktopPos = resolveDesktopPosition();
          if (desktopPos) {
            updateWidgetDesktopPosition(widgetId as WidgetId, desktopPos);
          }
        } else {
          // For icons: check if the raw intended drop position collides with another icon or folder
          const rawPos = calculateRawDropPosition(dropPoint, gridState, grabOffsetRef.current);
          const itemAtRawPos = rawPos
            ? gridState?.items.find(
                (item) =>
                  item.row === rawPos.row &&
                  item.col === rawPos.col &&
                  item.id !== data.id
              )
            : null;

          if (itemAtRawPos?.type === "icon") {
            // Merge two icons into a new folder
            window.dispatchEvent(
              new CustomEvent("desktop-icon-create-folder", {
                detail: { draggedAppId: data.id, targetAppId: itemAtRawPos.id, position: rawPos },
              })
            );
          } else if (itemAtRawPos?.type === "folder") {
            // Add dragged icon to existing folder
            window.dispatchEvent(
              new CustomEvent("desktop-icon-add-to-folder", {
                detail: { draggedAppId: data.id, folderId: itemAtRawPos.id },
              })
            );
          } else {
            // Normal move to free position
            const desktopPos = resolveDesktopPosition();
            if (desktopPos) {
              window.dispatchEvent(
                new CustomEvent("desktop-icon-moved", {
                  detail: { appId: data.id, position: desktopPos },
                })
              );
            }
          }
        }
        return;
      }

      if (source === "sidebar" && dropZone === "sidebar") {
        const sidebarWidgets = widgetConfigs
          .filter((w) => w.enabled && !w.pinnedToDesktop)
          .sort((a, b) => a.position - b.position);
        const targetIndex =
          sidebarDropIndex ??
          getSidebarDropIndex(overId ?? null, sidebarWidgets, dropPoint ?? undefined);
        moveWidgetWithinSidebar(widgetId as WidgetId, targetIndex);
        return;
      }

      if (dropZone === "store" && source !== "store") {
        disableWidget(widgetId as WidgetId);
      }

      setCurrentDesktopDropPosition(null);
      setIsDraggingDesktopWidget(false);

      // Deactivate grid overlay
      setGridDragActive(false);
      setHoveredGridPosition(null);
      setDraggedItemSize(null);

      if (edgeHotzoneTimeoutRef.current) {
        clearTimeout(edgeHotzoneTimeoutRef.current);
        edgeHotzoneTimeoutRef.current = null;
      }

      dragStartPointerRef.current = null;
      grabOffsetRef.current = null;
    },
    [
      widgetConfigs,
      moveWidgetToSidebar,
      moveWidgetWithinSidebar,
      moveWidgetToDesktop,
      disableWidget,
      updateWidgetDesktopPosition,
      gridState,
      setActiveWidget,
      setCurrentDesktopDropPosition,
      setDragOverlayRect,
      flushSidebarDropIndex,
      setGridDragActive,
      setHoveredGridPosition,
      setDraggedItemSize,
      windows,
      isWidgetAreaOpen,
    ]
  );

  const handleDragCancel = useCallback(() => {
    setActiveWidget(null);
    setActiveItem(null);
    setCurrentDesktopDropPosition(null);
    setDragOverlayRect(null);
    flushSidebarDropIndex(null);
    setIsDraggingDesktopWidget(false);

    // Deactivate grid overlay
    setGridDragActive(false);
    setHoveredGridPosition(null);
    setDraggedItemSize(null);

    if (edgeHotzoneTimeoutRef.current) {
      clearTimeout(edgeHotzoneTimeoutRef.current);
      edgeHotzoneTimeoutRef.current = null;
    }
    dragStartPointerRef.current = null;
    grabOffsetRef.current = null;
  }, [
    setActiveWidget,
    setCurrentDesktopDropPosition,
    setDragOverlayRect,
    flushSidebarDropIndex,
    setGridDragActive,
    setHoveredGridPosition,
    setDraggedItemSize,
  ]);

  // Monitor drag position to auto-open sidebar when near right edge
  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const pointerStart =
        dragStartPointerRef.current ?? extractClientPoint(event.activatorEvent);
      const initialRect = event.active.rect.current.initial;
      const hasFreshPointer =
        lastPointerEventTsRef.current > 0 &&
        Date.now() - lastPointerEventTsRef.current < 200;

      if (!hasFreshPointer) {
        if (!dragStartPointerRef.current && pointerStart) {
          dragStartPointerRef.current = pointerStart;
        }

        if (pointerStart) {
          cursorPositionRef.current = {
            x: pointerStart.x + event.delta.x,
            y: pointerStart.y + event.delta.y,
          };
        } else if (initialRect && typeof initialRect.left === "number") {
          cursorPositionRef.current = {
            x: initialRect.left + initialRect.width / 2 + event.delta.x,
            y: initialRect.top + initialRect.height / 2 + event.delta.y,
          };
        }
      }

      // Calculate potential drop position
      if (activeItem) {
        const dropPoint = cursorPositionRef.current;
        const dropZone = getDropZone(event.over?.id ?? null);

        // First check if cursor is in sidebar (priority check)
        const cursorInSidebar = isPointInsideSidebarArea(
          dropPoint,
          isWidgetAreaOpen
        );

        // Debug logging
        const isOverSidebar = cursorInSidebar || dropZone === "sidebar";
        const shouldShowSidebarPlaceholder =
          activeItem.type === "widget" && activeItem.source !== "sidebar";

        if (shouldShowSidebarPlaceholder && isOverSidebar) {
          const sidebarWidgets = widgetConfigs
            .filter((w) => w.enabled && !w.pinnedToDesktop)
            .sort((a, b) => a.position - b.position);
          const sidebarOverId =
            dropZone === "sidebar" ? event.over?.id ?? SIDEBAR_ZONE_ID : SIDEBAR_ZONE_ID;
          const targetIndex = getSidebarDropIndex(
            sidebarOverId,
            sidebarWidgets,
            dropPoint ?? undefined
          );
          scheduleSidebarDropIndex(targetIndex);
        } else {
          scheduleSidebarDropIndex(null);
        }

        // Determine if we are over the desktop
        let isOverDesktop = false;

        // If cursor is in sidebar OR dropZone is sidebar/store, definitely not over desktop
        if (cursorInSidebar || dropZone === "sidebar" || dropZone === "store") {
          isOverDesktop = false;
        } else if (dropZone === "desktop") {
          isOverDesktop = true;
        } else if (isDraggingDesktopWidget) {
          // If dragging desktop widget and not over sidebar/store, assume desktop
          isOverDesktop = true;
        }

        if (isOverDesktop) {
          if (!dropPoint || !gridState) {
            return;
          }

          const lastPosRef = (event.active.data.current as any)
            .lastCalculatedPos;

          const pos = calculateDesktopDropPosition(
            dropPoint,
            activeItem.size,
            gridState,
            grabOffsetRef.current,
            activeItem.id,
            activeItem.type
          );

          if (
            pos &&
            (!lastPosRef ||
              lastPosRef.row !== pos.row ||
              lastPosRef.col !== pos.col)
          ) {
            (event.active.data.current as any).lastCalculatedPos = pos;
            setCurrentDesktopDropPosition(pos);
            setHoveredGridPosition(pos);
          }
        } else {
          // Clear desktop drop position when over sidebar
          setCurrentDesktopDropPosition(null);
          setHoveredGridPosition(null);
        }
      }

      if (!isDraggingDesktopWidget) return;

      const { x: clientX } = cursorPositionRef.current;
      const screenWidth = window.innerWidth;
      const distanceFromRightEdge = screenWidth - clientX;

      // Only trigger sidebar when cursor is actually in the edge zone
      if (
        distanceFromRightEdge <= SIDEBAR_EDGE_TRIGGER_PX &&
        !isWidgetAreaOpen
      ) {
        if (!edgeHotzoneTimeoutRef.current) {
          edgeHotzoneTimeoutRef.current = window.setTimeout(() => {
            openWidgetArea();
            edgeHotzoneTimeoutRef.current = null;
          }, 100);
        }
      } else if (
        distanceFromRightEdge > SIDEBAR_EDGE_TRIGGER_PX &&
        edgeHotzoneTimeoutRef.current
      ) {
        clearTimeout(edgeHotzoneTimeoutRef.current);
        edgeHotzoneTimeoutRef.current = null;
      }
    },
    [
      isDraggingDesktopWidget,
      isWidgetAreaOpen,
      openWidgetArea,
      activeItem,
      widgetConfigs,
      gridState,
      setCurrentDesktopDropPosition,
      scheduleSidebarDropIndex,
      setHoveredGridPosition,
    ]
  );

  const dragOverlaySize = useMemo(() => {
    if (dragOverlayRect) {
      return dragOverlayRect;
    }
    if (!activeWidget) {
      return null;
    }

    return SIDEBAR_WIDGET_PREVIEW_SIZE[activeWidget.size];
  }, [activeWidget, dragOverlayRect]);

  return (
    <>
      <DndContext
        sensors={sensors}
        modifiers={[boundaryModifier]}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}

        {/* Visual edge hotzone indicator when dragging desktop widgets */}
        {isDraggingDesktopWidget && !isWidgetAreaOpen && (
          <div
            className="fixed top-0 right-0 bottom-0 w-[50px] z-[12999] pointer-events-none"
            style={{
              background:
                "linear-gradient(to left, rgba(59, 130, 246, 0.15), transparent)",
              borderLeft: "2px solid rgba(59, 130, 246, 0.3)",
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center text-blue-500/50">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        )}

        <DragOverlay dropAnimation={{ duration: 180 }} zIndex={14000}>
          {activeWidget &&
          dragOverlaySize &&
          !isDraggingDesktopWidget &&
          !shouldHideDragOverlay ? (
            <div
              className="pointer-events-none select-none rounded-3xl overflow-hidden border shadow-2xl"
              style={{
                width: dragOverlaySize.width,
                height: dragOverlaySize.height,
                borderColor: "var(--window-border-color)",
                backgroundColor: "var(--cdx-color-bg-elev-1)",
              }}
            >
              <WidgetPreviewProvider>
                {renderWidget(activeWidget.id, {
                  widgetId: activeWidget.id,
                  config: activeWidget,
                  onOpenApp: () => {},
                  onUpdateSettings: () => {},
                  staticPreview: true,
                })}
              </WidgetPreviewProvider>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}

// Helper: Calculate sidebar drop index from over ID or cursor position
function getSidebarDropIndex(
  overId: string | number | null,
  sidebarWidgets: WidgetConfig[],
  cursorPosition?: { x: number; y: number }
): number {
  if (!overId || typeof overId !== "string") return sidebarWidgets.length;

  const overWidgetId = extractWidgetId(overId);
  if (overWidgetId) {
    const index = sidebarWidgets.findIndex((w) => w.id === overWidgetId);
    return index === -1 ? sidebarWidgets.length : index;
  }

  if (overId === SIDEBAR_ZONE_ID) {
    const panel = getSidebarPanelElement();
    return determineSidebarDropIndex({
      panel,
      cursor: cursorPosition ?? null,
      totalWidgets: sidebarWidgets.length,
    });
  }

  return sidebarWidgets.length;
}

// Returns the raw intended grid position (no collision avoidance) for folder-merge detection
function calculateRawDropPosition(
  dropPoint: { x: number; y: number } | null,
  gridState: DesktopGridState | null,
  grabOffset: { x: number; y: number } | null = null,
): { row: number; col: number } | null {
  if (!dropPoint || !gridState) return null;
  const { offsetLeft, offsetTop, paddingX, paddingY, cellWidth, cellHeight, cols, rows } = gridState;
  const relativeX = dropPoint.x - offsetLeft - paddingX;
  const relativeY = dropPoint.y - offsetTop - paddingY;
  const adjustedX = grabOffset ? relativeX - grabOffset.x : relativeX - cellWidth / 2;
  const adjustedY = grabOffset ? relativeY - grabOffset.y : relativeY - cellHeight / 2;
  const col = Math.max(0, Math.min(cols - 1, Math.round(adjustedX / cellWidth)));
  const row = Math.max(0, Math.min(rows - 1, Math.round(adjustedY / cellHeight)));
  return { row, col };
}

function calculateDesktopDropPosition(
  dropPoint: { x: number; y: number } | null,
  size: WidgetSize | "icon",
  gridState: DesktopGridState | null,
  grabOffset: { x: number; y: number } | null = null,
  draggedItemId?: string,
  draggedItemType?: DesktopDragType
): WidgetDesktopPosition | null {
  if (!dropPoint || !gridState) {
    return null;
  }

  const {
    offsetLeft,
    offsetTop,
    paddingX,
    paddingY,
    cellWidth,
    cellHeight,
    cols,
    rows,
    occupiedCells,
    items,
  } = gridState;

  // Calculate relative coordinates of the drop point
  const relativeX = dropPoint.x - offsetLeft - paddingX;
  const relativeY = dropPoint.y - offsetTop - paddingY;

  const itemSize =
    size === "icon" ? { rows: 1, cols: 1 } : WIDGET_GRID_SIZE[size];

  // Apply grab offset if available to get the top-left corner index accurately
  const adjustedX = grabOffset
    ? relativeX - grabOffset.x
    : relativeX - (itemSize.cols * cellWidth) / 2;
  const adjustedY = grabOffset
    ? relativeY - grabOffset.y
    : relativeY - (itemSize.rows * cellHeight) / 2;

  const col = Math.max(
    0,
    Math.min(cols - itemSize.cols, Math.round(adjustedX / cellWidth))
  );
  const row = Math.max(
    0,
    Math.min(rows - itemSize.rows, Math.round(adjustedY / cellHeight))
  );

  const desiredPosition = { row, col };

  // Build occupied cells from both state set and items to ensure icons/widgets are respected
  const excludedOccupiedCells = new Set<string>(occupiedCells ?? []);
  if (items && items.length) {
    items.forEach((item) => {
      for (let r = 0; r < item.rows; r++) {
        for (let c = 0; c < item.cols; c++) {
          excludedOccupiedCells.add(`${item.row + r}:${item.col + c}`);
        }
      }
    });
  }

  // Exclude the currently dragged item from occupancy so it can move freely
  if (draggedItemId && draggedItemType) {
    const draggedItem = items.find(
      (item) => item.id === draggedItemId && item.type === draggedItemType
    );
    if (draggedItem) {
      // Remove all cells occupied by the dragged item
      for (let r = 0; r < draggedItem.rows; r++) {
        for (let c = 0; c < draggedItem.cols; c++) {
          const key = `${draggedItem.row + r}:${draggedItem.col + c}`;
          excludedOccupiedCells.delete(key);
        }
      }
    }
  }

  // Use unified layout utilities for collision and fallback
  const gridDim = { rows, cols };
  return unifiedFindNearestFreePosition(
    desiredPosition,
    itemSize,
    gridDim,
    excludedOccupiedCells
  );
}
